import { useRef, useState, useEffect } from 'react'
import { useFrame, useThree } from '@react-three/fiber'
import { useRouter } from 'next/router'
import { MathUtils, Vector3 } from 'three'
import { useAtom } from 'jotai'
import VirtualScroll from 'virtual-scroll'
import { cubicBezier } from 'framer-motion'
import { motion } from 'framer-motion-3d'
import { Col } from '@/components'
import { projectAtom, scrollPosAtom, isFirstVisitAtom } from '@/stores'
import { useScreenSize, useIsomorphicLayoutEffect, useIsClient } from '@/hooks'
import { type ArtworkType } from '@/@types/microcms-type'

const getPositions = (
    width: number,
    height: number,
    cols: number
): Vector3[] => {
    const SHIFT_AMOUNT = cols === 3 ? 0.3 : cols === 2 ? 0.15 : 0 // shift images in x axis
    const positions: Vector3[] = []
    for (let i = 0; i < cols; i++) {
        positions.push(
            new Vector3(
                (i - (cols - 1) / 2) * (width / cols) + SHIFT_AMOUNT * (i - 1),
                height / 2,
                0
            )
        )
    }
    return positions
}

export const Columns = () => {
    const [projects] = useAtom(projectAtom)
    const screenSize = useScreenSize()
    const [isFirst] = useAtom(isFirstVisitAtom)
    const [animationCompleted, setAnimationCompleted] = useState<boolean>(false)
    const [totalHeight, setTotalHeight] = useState<number[]>([0, 0, 0])
    const [scrollAmount, setScrollAmount] = useAtom(scrollPosAtom)
    const router = useRouter()
    const isClient = useIsClient()

    const {
        viewport: { width, height },
        size: { width: pxWidth }
    } = useThree()

    const ref = useRef<any>(null)

    // add margin bottom for texts area on mobile
    const MARGIN_BOTTOM = screenSize.XS ? pxWidth * 0.3 : pxWidth * 0.005
    const COLS_NUM = screenSize.XS ? 1 : screenSize.SM ? 2 : 3

    // calc total height of projects of each column
    const calcTotalHeight = (projects: ArtworkType[]) => {
        return projects.reduce((sum, project) => {
            const thumbImage =
                project.thumbnail_video?.poster ?? project.thumbnail_image
            const w = thumbImage?.width ?? 0
            const h = thumbImage?.height ?? 0
            return sum + (h * pxWidth) / COLS_NUM / w + MARGIN_BOTTOM
        }, 0)
    }

    useIsomorphicLayoutEffect(() => {
        if (!ref.current || typeof window === 'undefined' || !isClient) return
        const columns = ref.current

        columns.userData.target = {
            position: { y: scrollAmount ?? 0 }
        }

        if (!animationCompleted) return

        let scroller: VirtualScroll | null = null

        const initScroller = () => {
            if (scroller) {
                scroller.destroy()
            }

            scroller = new VirtualScroll({
                el: window,
                mouseMultiplier: 0.0025,
                touchMultiplier: 0.035,
                passive: true
            })

            scroller.on((event) => {
                if (columns.userData.target) {
                    columns.userData.target.position.y -= event.deltaY
                }
            })
        }

        initScroller()

        return () => {
            if (scroller) {
                scroller.destroy()
            }
        }
    }, [animationCompleted, isClient, scrollAmount])

    useEffect(() => {
        if (!ref.current || typeof window === 'undefined' || !isClient) return
        const columns = ref.current

        const beforeRouterUpdate = () => {
            const scrollPos = columns.userData.target.position.y
            setScrollAmount(scrollPos)
        }

        router.events.on('routeChangeStart', beforeRouterUpdate)
        return () => router.events.off('routeChangeStart', beforeRouterUpdate)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isClient])

    useFrame(() => {
        if (!ref.current || !isClient) return
        // lerp scroll
        const columns = ref.current
        columns.position.y = MathUtils.lerp(
            columns.position.y,
            columns.userData.target?.position.y ?? 0,
            0.08
        )
    })

    useIsomorphicLayoutEffect(() => {
        if (!projects || !isClient) return
        // recalc total height when window resized
        const columnProjects: ArtworkType[][] = projects.reduce(
            (columns, project, index) => {
                const columnIndex = index % COLS_NUM
                ;(columns[columnIndex] as ArtworkType[]).push(project)
                return columns
            },
            Array.from({ length: COLS_NUM }, () => [])
        )

        const handleResize = () => {
            setTotalHeight(
                columnProjects.map((projects) => calcTotalHeight(projects))
            )
        }
        handleResize()
    }, [pxWidth, projects, isClient])

    if (!projects || !isClient) return null

    const columnProjects: ArtworkType[][] = projects.reduce(
        (columns, project, index) => {
            const columnIndex = index % COLS_NUM
            ;(columns[columnIndex] as ArtworkType[]).push(project)
            return columns
        },
        Array.from({ length: COLS_NUM }, () => [])
    )

    const columnPosition = getPositions(width, height, COLS_NUM)

    const easeing = cubicBezier(0.03, 0.83, 0.1, 0.99)

    const variants = {
        initial: {
            y: isFirst ? scrollAmount - 40 : scrollAmount - 20
        },
        animate: {
            y: scrollAmount,
            transition: {
                duration: isFirst ? 3.2 : 1.6,
                ease: easeing
            }
        },
        exit: {
            y: scrollAmount,
            transition: {
                duration: 0
            }
        }
    }

    return (
        <motion.group
            variants={variants}
            initial="initial"
            animate={isFirst ? 'initial' : 'animate'}
            exit="exit"
            onAnimationComplete={() => setAnimationCompleted(true)}
            ref={ref}
        >
            {columnPosition.map((position, index) => (
                <Col
                    key={index}
                    projects={columnProjects[index]}
                    position={position}
                    totalHeight={totalHeight[index]}
                />
            ))}
        </motion.group>
    )
}
